import { Box, Stack, useTheme } from '@mui/material'
import { Chart } from 'chart.js'
import { memo, useEffect, useRef, useState } from 'react'
import algo from '../../core/Algo'
import { IProviderExpense } from '../../definitions/ServiceExpense'
import { HorizontalBarChart } from '../charts'
import { ChartDataWithUnit } from '../charts/ChartsHelper'
import LabeledChart from '../controls/data_visualization/LabeledChart'

interface P {
    totalExpense: ChartDataWithUnit<'bar', number[], string>
    providerExpense: IProviderExpense[]
    handlePdfGeneration: (content: { label: string, chart: string }[]) => void
}

const ReadyToPrintExpenseByTos = ({ totalExpense, providerExpense, handlePdfGeneration }: P) => {
    const theme = useTheme()
    const chartsRefs = useRef<(HTMLDivElement | null)[]>([])
    const readyIdsRef = useRef<Set<string>>(new Set())
    const [isProcessing, setIsProcessing] = useState(false)

    const totalElements = providerExpense.length + 1

    const handleChartFinishedAction = () => {
        if (isProcessing) return
        setIsProcessing(true)

        const base64charts = chartsRefs.current
            .filter(container => container !== null)
            .map(container => {
                const canvas = container.querySelector('canvas')
                const providerName = container.parentElement.dataset.providerName
                if (!canvas) return null
                const instance = Chart.getChart(canvas)
                return {
                    label: providerName ?? '',
                    chart: instance ? instance.toBase64Image() : null
                }
            })
            .filter(chartContainer => chartContainer.chart !== null)

        handlePdfGeneration(base64charts)
    }

    useEffect(() => {
        const updateChartsList = (e: any) => {
            const { chartId } = e.detail

            if (!readyIdsRef.current.has(chartId)) {
                readyIdsRef.current.add(chartId)
                if (readyIdsRef.current.size === totalElements) {
                    handleChartFinishedAction()
                }
            }
        }

        window.addEventListener('chart-finished', updateChartsList)

        return () => {
            window.removeEventListener('chart-finished', updateChartsList)
        }
    }, [totalElements])

    return (
        <Stack
            className='readyToPrintChart'
            sx={{
                position: 'fixed',
                left: '-5000px',
                top: 0,
                width: '1200px',
                height: 'auto',
                zIndex: -9999,
                backgroundColor: 'white'
            }}
            data-provider-name='Total RBCE'>

            {(totalExpense && providerExpense?.length > 0) && (
                <LabeledChart label='Total Rbce' ref={el => (chartsRefs.current[0] = el)}>
                    <HorizontalBarChart
                        data={totalExpense}
                        options={{
                            plugins: {
                                datalabels: {
                                    color: context => {
                                        const yAxisData = context.dataset.data as number[]
                                        const currentValue = yAxisData[context.dataIndex]
                                        const max = Math.max(...yAxisData)
                                        return (currentValue / max) < 0.5 ? 'rgba(0, 0, 0, .60)' : '#FFFFFF'
                                    },
                                    anchor: 'end',
                                    align: context => {
                                        const yAxisData = context.dataset.data as number[]
                                        const currentValue = yAxisData[context.dataIndex]
                                        const max = Math.max(...yAxisData)
                                        return (currentValue / max) < 0.5 ? 'end' : 'start'
                                    },
                                    offset: 10,
                                    formatter: value => '$ ' + algo.number(value)
                                },
                                legend: {
                                    display: false
                                }
                            },
                            scales: {
                                x: {
                                    display: false,
                                    grid: {
                                        display: false
                                    }
                                },
                                y: {
                                    grid: {
                                        display: false
                                    }
                                }
                            },
                            devicePixelRatio: 2
                        }}
                    />
                </LabeledChart>
            )}

            {totalExpense && providerExpense?.map((expense, i) => {
                return (
                    <Box key={i} data-provider-name={expense.providerName}>
                        <LabeledChart
                            label={expense.providerName}
                            ref={el => (chartsRefs.current[i + 1] = el)}>
                            <HorizontalBarChart
                                isLoading={false}
                                data={
                                    {
                                        labels: expense.expenses.map(e => e.name),
                                        datasets: [{
                                            data: expense.expenses.map(e => Math.floor(e.value)),
                                            backgroundColor: ['#1F6272', '#248E9A', '#00555A', '#00CDBA', '#013A3B']
                                        }]
                                    }
                                }
                                options={{
                                    plugins: {
                                        datalabels: {
                                            color: context => {
                                                const yAxisData = context.dataset.data as number[]
                                                const currentValue = yAxisData[context.dataIndex]
                                                const max = Math.max(...yAxisData)
                                                return (currentValue / max) < 0.5 ? 'rgba(0, 0, 0, .60)' : '#FFFFFF'
                                            },
                                            anchor: 'end',
                                            align: context => {
                                                const yAxisData = context.dataset.data as number[]
                                                const currentValue = yAxisData[context.dataIndex]
                                                const max = Math.max(...yAxisData)
                                                return (currentValue / max) < 0.5 ? 'end' : 'start'
                                            },
                                            offset: 10,
                                            formatter: value => '$ ' + algo.number(value)
                                        },
                                        legend: {
                                            display: false
                                        }
                                    },
                                    scales: {
                                        x: {
                                            display: false,
                                            grid: {
                                                display: false
                                            }
                                        },
                                        y: {
                                            grid: {
                                                display: false
                                            }
                                        }
                                    },
                                    devicePixelRatio: 2
                                }}
                            />
                        </LabeledChart>
                    </Box>
                )
            })}
        </Stack>
    )
}
export default memo(ReadyToPrintExpenseByTos)
